import React, { useEffect, useState } from 'react'
import { Toolbar, Typography, Avatar, Box, Divider, Badge } from '@mui/material'
import { styled } from '@mui/material/styles'
import Storage, { Auth } from 'storage/auth'
import { CHAT, PROFILE } from 'navigation/CONSTANTS'
import { useLocation } from 'react-router-dom'
import { IUserSession } from 'types'
import NavLinkBase from 'design-system/NavLink'
import MiniDrawer from '../Drawer'
import { HOME } from 'navigation/CONSTANTS'
import { getAuth } from 'firebase/auth'
import {
  ChatNotifications,
  useFirebaseListener
} from 'services/context/firebaseListener'
import EstablishmentMenu from './components/EstablishmentMenu'
import { useAppDispatch } from 'reduxStore/store'
import { cleanEstablishment } from 'reduxStore/slices'
import { useIsMobile } from 'utils/responsive-design'
import { ChatOutlined, DarkMode, LightMode } from '@mui/icons-material'
import { useThemeMode } from 'services/context/themeMode'
import IconButton from '@mui/material/IconButton'
import { useLogo } from 'hooks/useLogo'
import { wrapWithPermissionsByModuleName } from 'components/rules/PermissionByRole'

const StyledAppBar = styled(Box)(({ theme }) => ({
  // boxShadow: theme.shadows[2],
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.default
}))

const UserBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2)
}))

const StyledSignOut = styled(Typography)(() => ({
  cursor: 'pointer'
}))

const useHeader = () => {
  const location = useLocation()
  const [user, setUser] = useState<IUserSession>(
    Storage.user.get() as IUserSession
  )
  const [totalNotification, setTotalNotification] = useState<number>(0)
  const { chatNotifications } = useFirebaseListener()
  const dispatch = useAppDispatch()

  useEffect(() => {
    let total = 0
    for (const index in chatNotifications) {
      const key = index as keyof ChatNotifications
      if (key !== 'created_at') total += chatNotifications[key].count ?? 0
    }
    setTotalNotification(total)
  }, [chatNotifications])

  const handleLogout = () => {
    const fbAuth = getAuth()
    fbAuth.signOut()
    Auth.logout()
    dispatch(cleanEstablishment())
  }

  useEffect(() => {
    const handleStorageChange = () => {
      setUser(Storage.user.get() as IUserSession)
    }

    window.addEventListener('userImageUpdated', handleStorageChange)
    window.addEventListener('userUpdated', handleStorageChange)

    return () => {
      window.removeEventListener('userImageUpdated', handleStorageChange)
      window.removeEventListener('userUpdated', handleStorageChange)
    }
  }, [])

  return {
    user,
    totalNotification,
    handleLogout,
    location
  }
}

export const Header: React.FC = () => {
  const { user, totalNotification, handleLogout, location } = useHeader()
  const isMobile = useIsMobile()
  const { mode, toggleMode } = useThemeMode()
  const logo = useLogo()

  return (
    <StyledAppBar position='static'>
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: '56px!important'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <MiniDrawer mobile={isMobile} />
          <NavLinkBase
            to={HOME}
            style={{
              position: 'absolute',
              left: '64px'
            }}
            absolute
          >
            <Box
              component='img'
              src={logo}
              alt='sentinela logo'
              sx={{
                height: 51,
                display: {
                  xs: 'none',
                  sm: 'block'
                }
              }}
            />
          </NavLinkBase>
        </Box>
        <UserBox>
          {!isMobile && (
            <>
              <EstablishmentMenu />
              {wrapWithPermissionsByModuleName(
                (<NavLinkBase
                  to={CHAT}
                  absolute
                  style={{
                    color: 'var(--secondarySoft-button-color)'
                  }}
                >
                  <Badge badgeContent={totalNotification} color='error'>
                    <ChatOutlined />
                  </Badge>
                </NavLinkBase>),
                true,
                ['chat']
              )}
            </>
          )}
          <IconButton
            onClick={toggleMode}
            color='inherit'
            aria-label={
              mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
            }
          >
            {mode === 'dark' ? <LightMode /> : <DarkMode />}
          </IconButton>
          <NavLinkBase
            to={PROFILE}
            absolute
            style={{
              cursor: location.pathname === `/${PROFILE}` ? 'auto' : undefined,
              color: 'inherit'
            }}
          >
            <UserBox>
              <Avatar alt={user?.first_name} src={user?.user_image} />
              {!isMobile && (
                <Typography variant='body2'>{user?.first_name}</Typography>
              )}
            </UserBox>
          </NavLinkBase>
          <Divider orientation='vertical' variant='middle' />
          <StyledSignOut
            variant='body2'
            color='primarySoft'
            onClick={handleLogout}
          >
            Sair
          </StyledSignOut>
        </UserBox>
      </Toolbar>
    </StyledAppBar>
  )
}

export default Header
